// DatabaseSettingsFormRow.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// CSS
import '../views/DatabaseSettingsViewRow.css';

// Icons
import Icon from 'svg/Icon';

// Components
import Button from 'components/button/Button';

const DatabaseSettingsFormRow = React.memo(({
  badge = '',
  draggable = false,
  iconKey = 'dynamic_form',
  isDefault = false,
  isDragging = false,
  isDropTarget = false,
  label,
  locked = false,
  note,
  onDesignClick,
  onDragEnd,
  onDragOver,
  onDragStart,
  onDrop,
  onSettingsClick,
  showDragHandle = true,
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    // ----- RENDER ----- //

    return (
    <div
      className={`database-settings-view-row-container${isDragging ? ' dragging' : ''}${locked ? ' locked' : ''}`}
      style={{
        backgroundColor: isDropTarget ? theme.backgroundHover : theme.panel,
        borderColor: isDropTarget ? theme.foregroundMuted : theme.backgroundHover,
        color: theme.foreground,
        cursor: locked ? 'default' : (draggable ? 'move' : 'default'),
        opacity: locked ? 0.92 : 1,
      }}
      draggable={draggable && !locked}
      title={label || 'Form template'}
      onDragEnd={locked ? undefined : onDragEnd}
      onDragOver={locked ? undefined : onDragOver}
      onDragStart={locked ? undefined : onDragStart}
      onDrop={locked ? undefined : onDrop}
    >
      <div className="database-settings-view-row-main">
        <div className="database-settings-view-row-icon">
          <Icon
            iconKey={iconKey}
            width={18}
            height={18}
            color={theme.foregroundMuted}
          />
        </div>

        <div className="database-settings-view-row-copy">
          <div
            className="database-settings-view-row-label"
            style={{
              color: theme.foreground,
            }}
          >
            {label}
            {badge ? (
              <span
                className="database-settings-view-row-badge"
                style={{ color: theme.foregroundMuted }}
              >
                {badge}
              </span>
            ) : null}
          </div>

          {note ? (
            <div
              className="database-settings-view-row-description"
              style={{
                color: theme.foregroundMuted,
              }}
            >
              {note}
            </div>
          ) : null}
        </div>
      </div>

      <div className="database-settings-view-row-actions">
        {!locked ? (
          <Button
            title="Design form template"
            iconKey="design_services"
            iconSize={16}
            label="Edit Template"
            borderRadius="6px"
            height="30px"
            width="auto"
            onClick={(event) => {
              event.stopPropagation();
              onDesignClick?.();
            }}
          />
        ) : null}

        {!locked ? (
          <button
            className="database-settings-view-row-action"
            title="Form template settings"
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onSettingsClick?.();
            }}
          >
            <Icon
              iconKey="settings"
              width={16}
              height={16}
              color={theme.foregroundMuted}
            />
          </button>
        ) : (
          <div
            className="database-settings-view-row-action"
            title="System forms are edited in Admin → Types"
          >
            <Icon
              iconKey="shield"
              width={16}
              height={16}
              color={theme.foregroundMuted}
            />
          </div>
        )}

        {showDragHandle && !isDefault && !locked ? (
          <div
            className="database-settings-view-row-drag-handle"
            title="Drag to reorder"
          >
            <Icon
              iconKey="drag_indicator"
              width={18}
              height={18}
              color={theme.foregroundMuted}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
});

// Dev Tools
DatabaseSettingsFormRow.displayName = 'DatabaseSettingsFormRow';

export default DatabaseSettingsFormRow;
